import { useLazyGetFeaturesQuery } from 'api/featuresApi'
import { useEffect, useState } from 'react'
import { FeatureFlags } from 'shared/types'
import { getClientAnonymousUserId } from 'shared/utils/clientAnalyticsIds'
import { selectUserIsAuthenticated } from 'store/authSlice'
import { setFeatureFlags } from 'store/featureFlagsSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'

type UseFFConfig = {
  shouldRefetch: boolean
}

// Overload signatures
export function useFeatureFlag(name: string): boolean
export function useFeatureFlag(name: string, config: UseFFConfig): null | boolean

/**
 * @param name The name of the feature flag to check.
 * @param config Optional config object.
 * @param config.shouldRefetch If true, it will fetch the feature flag values from the server
 *                             and update the store with the new values.
 *                             If false, it will use the current values from the store.
 * @returns null | boolean
 *          - If `shouldRefetch` is false, it will return the current value of the feature flag from the store.
 *          - If `shouldRefetch` is true, it will return the fresh feature flag value from the server. While fetching,
 *            it will return `null`.
 *
 * @example
 * const freshFFforABTest = useFeatureFlag('someFlagName', { shouldRefetch: true })
 *
 * if (freshFFforABTest === null) {
 *   // i.e. do nothing or show loading state
 * } else {
 *   // proceed with returned value
 * }
 *
 */
export function useFeatureFlag(name: string, config: UseFFConfig = { shouldRefetch: false }) {
  const { featureFlags } = useAppSelector((state) => state.features)
  const { shouldRefetch } = config || {}
  const [freshFFValues, setFreshFFValues] = useState<FeatureFlags | null>(null)
  const [refetch] = useLazyGetFeaturesQuery()
  const storeDispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectUserIsAuthenticated)
  const auuid = getClientAnonymousUserId()

  const getFreshValues = async () => {
    try {
      const newFF = await refetch().unwrap()
      // eslint-disable-next-line no-console
      console.debug(
        `Fresh ${name}: ${!!newFF?.[name]} ${
          `${newFF.id}` === auuid ? '🟢' : `🟡 ${newFF.id} !==  ${auuid}`
        }`,
      )
      setFreshFFValues(newFF)
      storeDispatch(setFeatureFlags(newFF))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch fresh Feature Flags.', error)
    }
  }

  useEffect(() => {
    if (shouldRefetch) {
      setFreshFFValues(null)
      getFreshValues()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, auuid])

  return shouldRefetch ? !!freshFFValues?.[name] : !!featureFlags?.[name]
}
